import React from 'react';
import { Place } from './MapBetweenPlaces.tsx';

const TAG_CONFIG: Record<string, { icon: string; label: string }> = {
  'scenic':          { icon: 'ti-mountain',       label: 'Scenic' },
  'cafe-on-site':    { icon: 'ti-coffee',          label: 'Café on site' },
  'family-friendly': { icon: 'ti-friends',         label: 'Family friendly' },
  'good-for-walk':   { icon: 'ti-walk',            label: 'Good for a walk' },
  'dog-friendly':    { icon: 'ti-paw',             label: 'Dog friendly' },
  'farm-shop':       { icon: 'ti-building-store',  label: 'Farm shop' },
  'good-restaurant': { icon: 'ti-tools-kitchen-2', label: 'Good restaurant' },
};

interface PlaceDetailSheetProps {
  place: Place;
  relatedPlaces?: Place[];
  isSearching?: boolean;
  sheetRef?: React.RefObject<HTMLDivElement>;
  onSelectPlace?: (place: Place) => void;
  onClose: () => void;
}

const PlaceDetailSheet: React.FC<PlaceDetailSheetProps> = ({
  place,
  relatedPlaces = [],
  isSearching = false,
  sheetRef,
  onSelectPlace,
  onClose,
}) => {
  const categoryLabel = place.categories?.[0]?.name || 'Our Pick';
  const heroImage = place.images && place.images.length > 0 ? place.images[0] : null;
  const additionalImages = place.images && place.images.length > 1 ? place.images.slice(1) : [];

  const showSearchingIndicator = isSearching && relatedPlaces.length === 0;
  const showRelated = !isSearching && relatedPlaces.length > 0;

  return (
    <div className="place-detail-backdrop" onClick={onClose}>
      <div className="place-detail-sheet" ref={sheetRef} onClick={e => e.stopPropagation()}>

        {/* Sticky header — always visible, never scrolls away */}
        <div className="sheet-sticky-header">
          <div className="sheet-drag-handle" />
          <button className="sheet-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Scrollable content area */}
        <div className="sheet-scroll-body">

          {heroImage && heroImage.image_url && (
            <div className="sheet-hero">
              <img
                src={heroImage.image_url}
                alt={heroImage.alt_text || place.title}
                className="sheet-hero-img"
                onError={e => ((e.target as HTMLElement).parentElement!.style.display = 'none')}
              />
              {heroImage.attribution && (
                <div className="sheet-attr">
                  <a href={heroImage.original_source_url} target="_blank" rel="noopener noreferrer">
                    {heroImage.attribution}
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="sheet-body">
            <div className="sheet-header">
              <span className="sheet-category-label">{categoryLabel}</span>
              <h2 className="sheet-title">{place.title}</h2>
              <p className="sheet-city">{place.city}</p>
            </div>

            {place.description && (
              <div className="sheet-description" dangerouslySetInnerHTML={{ __html: place.description }} />
            )}

            {place.reason && (
              <div className="sheet-reason">{place.reason}</div>
            )}

            {additionalImages.map((img: any, i: number) => (
              <div key={i} className="sheet-additional-img-wrap">
                <img
                  src={img.image_url}
                  alt={img.alt_text || place.title}
                  className="sheet-additional-img"
                  onError={e => ((e.target as HTMLElement).parentElement!.style.display = 'none')}
                />
                {img.attribution && (
                  <p className="sheet-attr">
                    <a href={img.original_source_url} target="_blank" rel="noopener noreferrer">
                      {img.attribution}
                    </a>
                  </p>
                )}
              </div>
            ))}

            {place.tags && place.tags.length > 0 && (
              <div className="card-tags sheet-tags">
                {place.tags.map((tag: string) => {
                  const cfg = TAG_CONFIG[tag];
                  if (!cfg) return null;
                  return (
                    <span key={tag} className={`card-tag ${tag}`}>
                      <i className={`ti ${cfg.icon}`} aria-hidden="true" /> {cfg.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* ── Related places section ── */}
            {showSearchingIndicator && (
              <div className="sheet-related-searching">
                <span className="sheet-related-spinner" />
                Finding similar places along your route…
              </div>
            )}

            {showRelated && (
              <div className="sheet-related-section">
                <div className="sheet-related-heading">Similar Nearby Places</div>
                <div className="sheet-related-list">
                  {relatedPlaces.map(related => {
                    const img = related.images?.[0];
                    return (
                      <div
                        key={related.place_id || related.id}
                        className="sheet-related-card"
                        onClick={() => onSelectPlace?.(related)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && onSelectPlace?.(related)}
                      >
                        {img?.image_url && (
                          <img
                            src={img.image_url}
                            alt={img.alt_text || related.title}
                            className="sheet-related-img"
                            onError={e => ((e.target as HTMLElement).style.display = 'none')}
                          />
                        )}
                        <div className="sheet-related-text">
                          <p className="sheet-related-title">{related.title}</p>
                          <p className="sheet-related-city">{related.city || related.address}</p>
                          {(related.strapLine || related.description) && (
                            <p className="sheet-related-desc">
                              {related.strapLine || related.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceDetailSheet;
